'use client'

import { useQueryClient } from '@tanstack/react-query'
import { Crown } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { memo, useCallback, useMemo } from 'react'

import { PRIME_BUTTON_ACTIVE_CLASS } from './primeStyles'
import { FILTER_METADATA_KEY } from '@components/Filters/hooks/useFilterMetadata'
import { reconnectPrimeAwareSockets } from '@features/websocket/socketService'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@fp/ui-kit'
import { usePrimeMode } from '@hooks/usePrimeMode'
import { cn } from '@lib/cn'
import { IS_PRIME_MODE_ENABLED } from '@lib/constants'
import { setPrimeMode } from '@lib/primeMode'
import { useAppDispatch } from '@store/hooks'
import { resetAllTabFilters } from '@store/modules/filters/filterMetaSlice'
import { ConfirmActionButton } from '@ui/ConfirmActionButton'

// Detail routes whose record may not exist in the other prime mode — toggling there sends the
// user back to the resource index instead of a dead detail page.
const DETAIL_ROUTE_REGEX = /^\/(users|accounts)\/[^/]+/

export const PrimeModeToggle = memo(() => {
  const pathname = usePathname()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const qc = useQueryClient()
  const isPrimeMode = usePrimeMode()

  const detailResource = useMemo(() => pathname.match(DETAIL_ROUTE_REGEX)?.[1] ?? null, [pathname])

  const applyPrimeToggle = useCallback(
    (redirectTo: string) => {
      setPrimeMode(!isPrimeMode)
      reconnectPrimeAwareSockets()

      dispatch(resetAllTabFilters())

      router.replace(redirectTo, { scroll: false })

      // Closed filter drawers would never refetch, so their other-mode counts must go; open ones refetch below.
      qc.removeQueries({ queryKey: [FILTER_METADATA_KEY], type: 'inactive' })
      qc.invalidateQueries()
    },
    [qc, isPrimeMode, dispatch, router],
  )

  const handleToggle = useCallback(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : ''
    applyPrimeToggle(`${pathname}${hash}`)
  }, [applyPrimeToggle, pathname])

  const handleConfirm = useCallback(() => {
    if (detailResource) applyPrimeToggle(`/${detailResource}`)
  }, [applyPrimeToggle, detailResource])

  if (!IS_PRIME_MODE_ENABLED) return null

  const button = (
    <Button
      variant='ghost'
      size='sm'
      className={cn(
        'h-8 gap-1.5 px-2.5 text-xs tracking-wide transition-all duration-200 rounded-full',
        isPrimeMode
          ? `bg-card text-prime hover:!bg-card hover:!text-prime font-medium ${PRIME_BUTTON_ACTIVE_CLASS}`
          : 'text-foreground hover:!text-foreground font-normal',
      )}
      onClick={detailResource ? undefined : handleToggle}
      aria-pressed={isPrimeMode}
      aria-label={isPrimeMode ? 'Disable Prime mode' : 'Enable Prime mode'}
    >
      <Crown
        className={cn('h-4 w-4 transition-all', isPrimeMode && 'fill-current drop-shadow-[0_0_3px_var(--prime)]')}
      />
      Prime
    </Button>
  )

  if (detailResource) {
    return (
      <ConfirmActionButton
        button={button}
        title={isPrimeMode ? 'Disable Prime mode?' : 'Enable Prime mode?'}
        message={`Switching Prime mode reloads all data and returns you to the ${detailResource} list. Continue?`}
        tooltipText={isPrimeMode ? 'Prime mode on' : 'Enable Prime mode'}
        onConfirm={handleConfirm}
      />
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side='bottom'>{isPrimeMode ? 'Prime mode on' : 'Enable Prime mode'}</TooltipContent>
    </Tooltip>
  )
})

PrimeModeToggle.displayName = 'PrimeModeToggle'
